import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ListTodo, Check, AlarmClock, Zap, ChevronRight, ChevronDown, AlertCircle, ArrowRight, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const priorityColors = {
  High: 'bg-red-500/15 text-red-400',
  Medium: 'bg-amber-500/15 text-amber-400',
  Low: 'bg-zinc-500/15 text-zinc-400',
};

const SECTIONS = [
  { key: 'overdue', label: 'Overdue', tone: 'text-red-400' },
  { key: 'due_today', label: 'Due Today', tone: 'text-amber-400' },
  { key: 'high_priority', label: 'High Priority', tone: 'text-orange-300' },
  { key: 'upcoming', label: 'Upcoming', tone: 'text-muted-foreground' },
];

function FocusTaskRow({ t, sectionKey, onDone, onSnooze }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/[0.06] last:border-0 gap-2" data-testid={`focus-task-${t.id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-normal truncate">{t.title}</p>
        <p className="text-xs text-muted-foreground truncate">
          {t.due_date && <span className={`tabular-nums ${sectionKey === 'overdue' ? 'text-red-400' : ''}`}>Due: {t.due_date}</span>}
          {t.due_date && t.project_label && ' · '}
          {t.project_label}
        </p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {t.priority && <Badge variant="outline" className={`text-xs ${priorityColors[t.priority] || ''}`}>{t.priority}</Badge>}
        <Badge variant="outline" className="text-xs hidden sm:inline-flex">{t.status}</Badge>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-amber-400" title="Snooze" data-testid={`focus-snooze-${t.id}`}>
              <AlarmClock className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-card border-border">
            <DropdownMenuItem className="text-xs" onClick={() => onSnooze(t, 1)} data-testid={`focus-snooze-1d-${t.id}`}>Snooze 1 day</DropdownMenuItem>
            <DropdownMenuItem className="text-xs" onClick={() => onSnooze(t, 3)}>Snooze 3 days</DropdownMenuItem>
            <DropdownMenuItem className="text-xs" onClick={() => onSnooze(t, 7)}>Snooze 1 week</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-emerald-400" title="Mark done" onClick={() => onDone(t)} data-testid={`focus-done-${t.id}`}>
          <Check className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

export function TodayFocusCard({ navigate, onTasksChanged }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [reminderOpen, setReminderOpen] = useState(false);

  const refresh = useCallback(() => {
    api.get('/dashboard/focus').then(r => setData(r.data)).catch(() => setData({}));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const markDone = async (t) => {
    try {
      await api.put(`/tasks/${t.id}`, { status: 'Completed' });
      toast.success('To-Do completed');
      refresh();
      if (onTasksChanged) onTasksChanged();
    } catch { toast.error('Could not complete To-Do'); }
  };

  const snooze = async (t, days) => {
    try {
      const r = await api.post(`/tasks/${t.id}/snooze`, { days });
      toast.success(`Snoozed until ${r.data.due_date}`);
      refresh();
    } catch { toast.error('Could not snooze To-Do'); }
  };

  const dismissReminder = async (r) => {
    try {
      await api.put(`/action-item-reminders/${r.id}/dismiss`);
      refresh();
    } catch { /* best-effort */ }
  };

  const convertReminder = async (r) => {
    try {
      const text = r.action_items || '';
      await api.post('/tasks', {
        title: text.length > 80 ? `${text.slice(0, 80)}...` : text,
        description: `From status update on ${r.project_name}: ${text}`,
        assigned_to: user?.id,
        priority: 'Medium',
      });
      await api.put(`/action-item-reminders/${r.id}/dismiss`);
      toast.success('Added to your To-Dos');
      refresh();
      if (onTasksChanged) onTasksChanged();
    } catch { toast.error('Could not create To-Do'); }
  };

  if (!data) return null;
  const taskSections = SECTIONS.filter(s => (data[s.key] || []).length > 0);
  const actionHour = data.action_hour || [];
  const reminders = data.action_items || [];
  const isEmpty = taskSections.length === 0 && actionHour.length === 0 && reminders.length === 0;

  return (
    <Card className="border-white/10 bg-card shadow-none" data-testid="today-focus-card">
      <CardHeader className="pb-2 border-b border-white/[0.08]">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <ListTodo className="h-3.5 w-3.5 text-primary shrink-0" /> Today&apos;s Focus
          <span className="text-xs text-muted-foreground/70 font-normal" data-testid="focus-open-count">{data.open_count || 0} open to-dos</span>
          <button type="button" className="ml-auto flex items-center gap-0.5 text-xs text-muted-foreground hover:text-primary transition-colors" onClick={() => navigate('/tasks')} data-testid="focus-view-all">
            View all <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-2 space-y-1">
        {isEmpty && <p className="text-xs text-muted-foreground py-2">All clear. Nothing needs your attention today.</p>}

        {taskSections.map(sec => (
          <div key={sec.key} data-testid={`focus-section-${sec.key}`}>
            <p className={`text-[10px] uppercase tracking-[0.08em] font-semibold pt-1.5 ${sec.tone}`}>
              {sec.label} <span className="font-mono tabular-nums">({data[sec.key].length})</span>
            </p>
            {data[sec.key].map(t => (
              <FocusTaskRow key={t.id} t={t} sectionKey={sec.key} onDone={markDone} onSnooze={snooze} />
            ))}
          </div>
        ))}

        {actionHour.length > 0 && (
          <div data-testid="focus-action-hour">
            <p className="text-[10px] uppercase tracking-[0.08em] font-semibold pt-1.5 text-amber-400 flex items-center gap-1">
              <Zap className="h-3 w-3" /> Action Hour <span className="font-mono tabular-nums">({actionHour.length})</span>
            </p>
            {actionHour.map(e => (
              <button
                key={e.id}
                type="button"
                className="w-full text-left flex items-center justify-between py-2 border-b border-white/[0.06] last:border-0 gap-2 hover:bg-secondary/20 transition-colors rounded-sm"
                onClick={() => navigate(`/projects/${e.project_id}?tab=status`)}
                data-testid={`focus-ah-${e.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-normal truncate">{e.project_label}</p>
                  {e.latest_status && <p className="text-xs text-muted-foreground truncate">{e.latest_status}</p>}
                </div>
                <Badge variant="outline" className="text-xs shrink-0">{e.phase}</Badge>
              </button>
            ))}
          </div>
        )}

        {reminders.length > 0 && (
          <Collapsible open={reminderOpen} onOpenChange={setReminderOpen}>
            <CollapsibleTrigger asChild>
              <button type="button" className="w-full flex items-center gap-1.5 pt-2 pb-1 text-[10px] uppercase tracking-[0.08em] font-semibold text-primary hover:text-primary/80 transition-colors" data-testid="focus-reminders-toggle">
                <AlertCircle className="h-3 w-3" /> Action Items on My Projects
                <span className="font-mono tabular-nums">({reminders.length})</span>
                <ChevronDown className={`h-3 w-3 ml-auto transition-transform ${reminderOpen ? 'rotate-180' : ''}`} />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              {reminders.map(r => (
                <div key={r.id} className="flex items-start justify-between py-2 border-b border-white/[0.06] last:border-0 gap-2" data-testid={`focus-reminder-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-normal">{r.action_items}</p>
                    <p className="text-xs text-muted-foreground truncate">{r.project_name}{r.created_by_name ? ` · by ${r.created_by_name}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="ghost" className="h-6 text-xs text-primary hover:text-primary/80 gap-1 px-1.5" onClick={() => convertReminder(r)} data-testid={`focus-reminder-convert-${r.id}`}>
                      <ArrowRight className="h-3 w-3" /> To-Do
                    </Button>
                    <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-foreground" title="Dismiss" onClick={() => dismissReminder(r)} data-testid={`focus-reminder-dismiss-${r.id}`}>
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
}
